import React, { useState, useEffect } from 'react';
import InputField from './ui/InputField';
import SelectField from './ui/SelectField';
import Slider from './ui/Slider';
import Checkbox from './ui/Checkbox';
import FileUpload from './ui/FileUpload';
import Button from './ui/Button';
import GoogleAddressInput from './ui/GoogleAddressInput';
import { LoanFormData, FormErrors } from '../types';
import { validateForm, isFormValid } from '../utils/formValidation';
import i18n from '../i18n/config';
import { submitLoanApplication } from '../utils/api';
import { initializeGoogleMaps } from '../utils/googleMaps';

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

interface LoanFormProps {
  onSubmit: (formData: LoanFormData) => void;
  onLoading: (isLoading: boolean, message?: string) => void;
}

const LoanForm: React.FC<LoanFormProps> = ({ onSubmit, onLoading }) => {
  const initialFormState: LoanFormData = {
    firstName: '',
    lastName: '',
    personalCode: '',
    email: '',
    phone: '',
    city: '',
    loanAmount: 10000,
    loanPeriod: 24,
    loanType: 'consumer',
    declaredAddress: '',
    facticalAddress: '',
    addressesMatch: true,
    dependentPersons: 0,
    bankAccount: '',
    documents: [],
    agreeToTerms: false,
    familyStatus: '',
    jobType: '',
  };
  
  const [formData, setFormData] = useState<LoanFormData>(initialFormState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [maxLoanAmount, setMaxLoanAmount] = useState(20000);
  const [minLoanAmount, setMinLoanAmount] = useState(100);
  const [stepSize, setStepSize] = useState(100);
  const [currentStep, setCurrentStep] = useState(1);
  const [hubspotContactId, setHubspotContactId] = useState<string | null>(() => {
    return localStorage.getItem('hubspotContactId');
  });

  const stepFields = {
    1: ['firstName', 'lastName', 'personalCode', 'email', 'phone', 'agreeToTerms'],
    2: ['loanType', 'loanAmount', 'loanPeriod'],
    3: ['city', 'declaredAddress'],
    4: ['dependentPersons', 'familyStatus', 'jobType'],
    5: ['bankAccount', 'documents', 'agreeToTerms'],
  };
  
  const loanTypeOptions = [
    { value: 'consumer', label: i18n.t('form.loan_types.consumer') },
    { value: 'mortgage', label: i18n.t('form.loan_types.mortgage') },
    { value: 'carLeasing', label: i18n.t('form.loan_types.car') },
    { value: 'business', label: i18n.t('form.loan_types.business') },
  ];
  
  const dependentPersonsOptions = [
    { value: '0', label: '0' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5+' },
  ];

  const familyStatusOptions = [
    { value: 'single', label: i18n.t('form.family_status.single') },
    { value: 'married', label: i18n.t('form.family_status.married') },
  ];

  const jobTypeOptions = [
    { value: 'permanent', label: i18n.t('form.job_type.permanent') },
    { value: 'temporary', label: i18n.t('form.job_type.temporary') },
    { value: 'self_employed', label: i18n.t('form.job_type.self_employed') },
    { value: 'retired', label: i18n.t('form.job_type.retired') },
    { value: 'student', label: i18n.t('form.job_type.student') },
    { value: 'unemployed', label: i18n.t('form.job_type.unemployed') },
    { value: 'other', label: i18n.t('form.job_type.other') },
  ];
  
  useEffect(() => {
    const loanTypeConfig = {
      consumer: { max: 20000, step: 100, min: 100 },
      mortgage: { max: 500000, step: 5000, min: 1000 },
      carLeasing: { max: 100000, step: 500, min: 500 },
      business: { max: 400000, step: 5000, min: 1000 },
    };
    
    const config = loanTypeConfig[formData.loanType];
    setMaxLoanAmount(config.max);
    setStepSize(config.step);
    setMinLoanAmount(config.min);
    
    if (formData.loanAmount > config.max) {
      setFormData(prev => ({
        ...prev,
        loanAmount: config.max,
      }));
    }
  }, [formData.loanType]);
  
  useEffect(() => {
    if (hubspotContactId) {
      localStorage.setItem('hubspotContactId', hubspotContactId);
    } else {
      localStorage.removeItem('hubspotContactId');
    }
  }, [hubspotContactId]);
  
  useEffect(() => {
    const initMaps = async () => {
      try {
        await initializeGoogleMaps();
      } catch (error) {
        console.error('Failed to initialize Google Maps:', error);
      }
    };
    
    initMaps();
  }, []);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'bankAccount') {
      setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    setTouched(prev => ({ ...prev, [name]: true }));
  };
  
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const newErrors = validateForm(formData);
    setErrors(prev => ({
      ...prev,
      [name]: newErrors[name]
    }));
  };
  
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
  };
  
  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
    setTouched(prev => ({ ...prev, [name]: true }));
    
    if (name === 'addressesMatch' && checked) {
      setFormData(prev => ({ ...prev, facticalAddress: prev.declaredAddress }));
    }
  };
  
  const handleFileChange = (files: File[]) => {
    setFormData(prev => {
      const existingFiles = prev.documents;
      const newFiles = files.filter(file => 
        !existingFiles.some(existing => 
          existing.name === file.name && existing.size === file.size
        )
      );
      
      return {
        ...prev, 
        documents: [...existingFiles, ...newFiles]
      };
    });
    setTouched(prev => ({ ...prev, documents: true }));
  };
  
  useEffect(() => {
    const touchedFields = Object.keys(touched).filter(key => touched[key]);
    if (touchedFields.length > 0) {
      const newErrors = validateForm(formData);
      const filteredErrors: FormErrors = {};
      
      touchedFields.forEach(field => {
        if (newErrors[field]) {
          filteredErrors[field] = newErrors[field];
        }
      });
      
      setErrors(filteredErrors);
    }
  }, [formData, touched]);
  
  const validateStep = (step: number): boolean => {
    const fieldsToValidate = stepFields[step as keyof typeof stepFields];
    const newErrors = validateForm(formData);
    
    const hasStepErrors = fieldsToValidate.some(field => newErrors[field]);
    return !hasStepErrors;
  };
  
  const handleNextStep = async (e: React.MouseEvent) => {
    e.preventDefault();

    const currentStepFields = stepFields[currentStep as keyof typeof stepFields];
    setTouched(prev => ({
      ...prev,
      ...Object.fromEntries(currentStepFields.map(field => [field, true]))
    }));

    if (validateStep(currentStep)) {
      if (currentStep === 1) {
        const formDataToSubmit = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
          if (['firstName', 'lastName', 'personalCode', 'email', 'phone', 'city'].includes(key)) {
            formDataToSubmit.append(key, String(value));
          }
        });
        
        submitLoanApplication(formDataToSubmit)
          .then(response => {
            if (response.success && response.data?.contact_id) {
              setHubspotContactId(response.data.contact_id);
            } else {
              console.error(i18n.t('form.messages.error', { message: response.error }));
            }
          })
          .catch(error => {
            console.error(i18n.t('form.messages.error', { message: error }));
          });
        
        setCurrentStep(2);
      } else if (currentStep < 5) {
        setCurrentStep(prev => prev + 1);
      }
    }
  };
  
  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep(currentStep)) {
      return;
    }
    
    try {
      onLoading(true, i18n.t('form.messages.processing'));
      
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key !== 'documents') {
          formDataToSubmit.append(key, String(value));
        }
      });
      
      formData.documents.forEach(file => {
        formDataToSubmit.append('documents[]', file);
      });
      
      if (hubspotContactId) {
        formDataToSubmit.append('contactId', hubspotContactId);
      }
      
      const response = await submitLoanApplication(formDataToSubmit);
      
      if (response.success) {
        localStorage.removeItem('hubspotContactId');
        setHubspotContactId(null);
        onSubmit(formData);
      } else {
        console.error(i18n.t('form.messages.error', { message: response.error }));
      }
    } catch (error) {
      console.error(i18n.t('form.messages.error', { message: error }));
    } finally {
      onLoading(false);
    }
  };
  
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <h3 className="text-lg font-medium text-primary-600 mb-4">{i18n.t('form.sections.personal_info')}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label={i18n.t('form.fields.first_name')}
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                error={touched.firstName ? errors.firstName : undefined}
                required
              />
              
              <InputField
                label={i18n.t('form.fields.last_name')}
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                error={touched.lastName ? errors.lastName : undefined}
                required
              />
            </div>
            
            <InputField
              label={i18n.t('form.fields.personal_code')}
              name="personalCode"
              value={formData.personalCode}
              onChange={handleInputChange}
              onBlur={handleBlur}
              error={touched.personalCode ? errors.personalCode : undefined}
              placeholder={i18n.t('form.placeholders.personal_code')}
              required
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label={i18n.t('form.fields.email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleBlur}
                error={touched.email ? errors.email : undefined}
                required
              />
              
              <InputField
                label={i18n.t('form.fields.phone')}
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                onBlur={handleBlur}
                error={touched.phone ? errors.phone : undefined}
                placeholder={i18n.t('form.placeholders.phone')}
                required
              />
            </div>
            
            <div className="mt-8">
              <Checkbox
                label={
                  <span className="text-base">
                    {i18n.t('form.fields.agree_to_terms')}{' '}
                    <a href={`/documents/${i18n.locale}/terms.pdf`} target="_blank" rel="noopener noreferrer" className="text-warning-600 hover:text-warning-800 font-medium">
                      {i18n.t('form.links.terms')}
                    </a>
                    {' '}{i18n.t('form.and')}{' '}
                    <a href={`/documents/${i18n.locale}/privacy_policy.pdf`} target="_blank" rel="noopener noreferrer" className="text-warning-600 hover:text-warning-800 font-medium">
                      {i18n.t('form.links.privacy_policy')}
                    </a>
                  </span>
                }
                checked={formData.agreeToTerms}
                onChange={(checked) => handleCheckboxChange('agreeToTerms', checked)}
                error={touched.agreeToTerms ? errors.agreeToTerms : undefined}
                className="text-base"
              />
            </div>
          </div>
        );
        
      case 2:
        return (
          <div>
            <h3 className="text-lg font-medium text-primary-600 mb-4">{i18n.t('form.sections.loan_details')}</h3>
            
            <SelectField
              label={i18n.t('form.fields.loan_type')}
              options={loanTypeOptions}
              value={formData.loanType}
              onChange={(value) => {
                handleSelectChange('loanType', value);
                setTouched(prev => ({ ...prev, loanType: true }));
              }}
              error={touched.loanType ? errors.loanType : undefined}
              required
            />
            
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Slider
                  label={i18n.t('form.fields.loan_amount')}
                  min={minLoanAmount}
                  max={maxLoanAmount}
                  step={stepSize}
                  value={formData.loanAmount}
                  onChange={(value) => {
                    setFormData(prev => ({ ...prev, loanAmount: value }));
                    setTouched(prev => ({ ...prev, loanAmount: true }));
                  }}
                  formatValue={(value) => (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={value}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue === '') {
                            setFormData(prev => ({ ...prev, loanAmount: minLoanAmount }));
                          } else {
                            const numberValue = Number(inputValue);
                            if (!isNaN(numberValue)) {
                              setFormData(prev => ({ ...prev, loanAmount: numberValue }));
                            }
                          }
                          setTouched(prev => ({ ...prev, loanAmount: true }));
                        }}
                        onBlur={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue === '' || Number(inputValue) === 0) {
                            setFormData(prev => ({ ...prev, loanAmount: minLoanAmount }));
                          } else {
                            const newValue = Math.max(Number(inputValue), minLoanAmount);
                            setFormData(prev => ({ ...prev, loanAmount: Math.min(newValue, maxLoanAmount) }));
                          }
                          setTouched(prev => ({ ...prev, loanAmount: true }));
                        }}
                        className="w-24 px-2 py-1 text-secondary-500 font-medium border border-secondary-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary-500 focus:border-secondary-500 bg-white"
                        min={minLoanAmount}
                        max={maxLoanAmount}
                        step={stepSize}
                      />
                      <span>€</span>
                    </div>
                  )}
                  unit=""
                />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Slider
                  label={i18n.t('form.fields.loan_period')}
                  min={3}
                  max={120}
                  step={3}
                  value={formData.loanPeriod}
                  onChange={(value) => {
                    setFormData(prev => ({ ...prev, loanPeriod: value }));
                    setTouched(prev => ({ ...prev, loanPeriod: true }));
                  }}
                  formatValue={(value) => (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={value}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue === '') {
                            setFormData(prev => ({ ...prev, loanPeriod: 3 }));
                          } else {
                            const numberValue = Number(inputValue);
                            if (!isNaN(numberValue)) {
                              setFormData(prev => ({ ...prev, loanPeriod: numberValue }));
                            }
                          }
                          setTouched(prev => ({ ...prev, loanPeriod: true }));
                        }}
                        onBlur={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue === '' || Number(inputValue) === 0) {
                            setFormData(prev => ({ ...prev, loanPeriod: 3 }));
                          } else {
                            const newValue = Math.max(Number(inputValue), 3);
                            setFormData(prev => ({ ...prev, loanPeriod: Math.min(newValue, 120) }));
                          }
                          setTouched(prev => ({ ...prev, loanPeriod: true }));
                        }}
                        className="w-16 px-2 py-1 text-secondary-500 font-medium border border-secondary-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary-500 focus:border-secondary-500 bg-white"
                        min={3}
                        max={120}
                        step={3}
                      />
                      <span>{i18n.t('form.months')}</span>
                    </div>
                  )}
                  unit=""
                />
              </div>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div>
            <h3 className="text-lg font-medium text-primary-600 mb-4">{i18n.t('form.sections.address_info')}</h3>
            
            <InputField
              label={i18n.t('form.fields.city')}
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              onBlur={handleBlur}
              error={touched.city ? errors.city : undefined}
              placeholder={i18n.t('form.placeholders.city')}
              required
            />
            
            <GoogleAddressInput
              label={i18n.t('form.fields.declared_address')}
              name="declaredAddress"
              value={formData.declaredAddress}
              onChange={(value) => {
                setFormData(prev => ({ 
                  ...prev, 
                  declaredAddress: value,
                  facticalAddress: formData.addressesMatch ? value : prev.facticalAddress 
                }));
                setTouched(prev => ({ ...prev, declaredAddress: true }));
              }}
              onBlur={() => handleBlur({ target: { name: 'declaredAddress' } } as React.FocusEvent<HTMLInputElement>)}
              error={touched.declaredAddress ? errors.declaredAddress : undefined}
              required
            />
            
            <Checkbox
              label={i18n.t('form.fields.addresses_match')}
              checked={formData.addressesMatch}
              onChange={(checked) => handleCheckboxChange('addressesMatch', checked)}
              className="mb-4"
            />
            
            {!formData.addressesMatch && (
              <GoogleAddressInput
                label={i18n.t('form.fields.factical_address')}
                name="facticalAddress"
                value={formData.facticalAddress}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, facticalAddress: value }));
                  setTouched(prev => ({ ...prev, facticalAddress: true }));
                }}
                onBlur={() => handleBlur({ target: { name: 'facticalAddress' } } as React.FocusEvent<HTMLInputElement>)}
                error={touched.facticalAddress ? errors.facticalAddress : undefined}
              />
            )}
          </div>
        );
        
      case 4:
        return (
          <div>
            <h3 className="text-lg font-medium text-primary-600 mb-4">{i18n.t('form.sections.family_info')}</h3>
            
            <SelectField
              label={i18n.t('form.fields.dependent_persons')}
              options={dependentPersonsOptions}
              value={formData.dependentPersons.toString()}
              onChange={(value) => {
                handleSelectChange('dependentPersons', value);
                setTouched(prev => ({ ...prev, dependentPersons: true }));
              }}
              error={touched.dependentPersons ? errors.dependentPersons : undefined}
              required
              className="mb-4"
            />

            <SelectField
              label={i18n.t('form.fields.family_status')}
              options={familyStatusOptions}
              value={formData.familyStatus}
              onChange={(value) => {
                handleSelectChange('familyStatus', value);
                setTouched(prev => ({ ...prev, familyStatus: true }));
              }}
              error={touched.familyStatus ? errors.familyStatus : undefined}
              required
              className="mb-4"
            />

            <SelectField
              label={i18n.t('form.fields.job_type')}
              options={jobTypeOptions}
              value={formData.jobType}
              onChange={(value) => {
                handleSelectChange('jobType', value);
                setTouched(prev => ({ ...prev, jobType: true }));
              }}
              error={touched.jobType ? errors.jobType : undefined}
              required
            />
          </div>
        );
        
      case 5:
        return (
          <div>
            <h3 className="text-lg font-medium text-primary-600 mb-4">{i18n.t('form.sections.banking_info')}</h3>
            
            <InputField
              label={i18n.t('form.fields.bank_account')}
              name="bankAccount"
              value={formData.bankAccount}
              onChange={handleInputChange}
              onBlur={handleBlur}
              error={touched.bankAccount ? errors.bankAccount : undefined}
              placeholder={i18n.t('form.placeholders.bank_account')}
            />
            
            <div className="mt-8">
              <h3 className="text-lg font-medium text-primary-600 mb-4">{i18n.t('form.fields.upload_documents')}</h3>
              
              <FileUpload
                label={i18n.t('form.documents_help')}
                onChange={handleFileChange}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                multiple={true}
                maxSize={5 * 1024 * 1024}
                error={touched.documents ? errors.documents : undefined}
                messages={{
                  error: i18n.t('form.messages.file_upload_error'),
                  success: i18n.t('form.messages.file_upload_success'),
                  removed: i18n.t('form.messages.file_upload_removed'),
                  max: i18n.t('form.messages.file_upload_max'),
                  size: i18n.t('form.messages.file_upload_size'),
                  type: i18n.t('form.messages.file_upload_type')
                }}
              />
            </div>

            <div className="mt-8">
              <Checkbox
                label={
                  <span className="text-base">
                    {i18n.t('form.fields.agree_to_terms')}{' '}
                    <a href={`/documents/${i18n.locale}/terms.pdf`} target="_blank" rel="noopener noreferrer" className="text-warning-600 hover:text-warning-800 font-medium">
                      {i18n.t('form.links.terms')}
                    </a>
                    {' '}{i18n.t('form.and')}{' '}
                    <a href={`/documents/${i18n.locale}/privacy_policy.pdf`} target="_blank" rel="noopener noreferrer" className="text-warning-600 hover:text-warning-800 font-medium">
                      {i18n.t('form.links.privacy_policy')}
                    </a>
                  </span>
                }
                checked={formData.agreeToTerms}
                onChange={(checked) => handleCheckboxChange('agreeToTerms', checked)}
                error={touched.agreeToTerms ? errors.agreeToTerms : undefined}
                className="text-base"
              />
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="bg-white w-full box-border p-4 md:p-8 rounded-lg h-full flex flex-col" id={i18n.t('anchor_links.loan_application_form')}>
      <h2 className="text-2xl font-bold text-primary-700 mb-4">{i18n.t('form.title')}</h2>
      
      <div className="mb-6">
        <div className="flex justify-between align-top items-center">
          {[1, 2, 3, 4, 5].map((step) => {
            let stepBg = 'bg-gray-200';
            let stepText = 'text-gray-400';
            if (step <= currentStep) {
              stepBg = 'bg-[' + '#1abc9c' + ']';
              stepText = 'text-white';
            } 
            return (
              <div
                key={step}
                className={`flex-1 text-center self-start`}
              >
                <div
                  className={`w-10 h-10 mx-auto rounded-lg flex items-center justify-center text-lg font-medium ${stepText} ${stepBg}`}
                >
                  {step}
                </div>
                
                <div className="mt-2 text-xs sm:text-sm text-gray-600 px-1 sm:px-3">
                  {i18n.t(`form.steps.${step}`)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-8 flex-1 flex flex-col">
        <div className="flex-1">
          {renderStep()}
        </div>
        
        <div className="flex justify-between mt-8">
          {currentStep > 1 && (
            <Button
              type="button"
              variant="secondary"
              onClick={handlePrevStep}
            >
              {i18n.t('form.buttons.previous')}
            </Button>
          )}
          
          {currentStep < 5 ? (
            <Button
              type="button"
              variant="primary"
              onClick={handleNextStep}
              className="ml-auto"
            >
              {i18n.t('form.buttons.next')}
            </Button>
          ) : (
            <Button
              type="submit"
              variant="primary"
              className="ml-auto"
            >
              {i18n.t('form.submit')}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default LoanForm;